import { localStorageColorSchemeManager, MantineProvider } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { ModalsProvider } from '@mantine/modals'
import { NavigationProgress } from '@mantine/nprogress'
import type { Locale } from '@tnn/core'
import { type ReactNode, useMemo } from 'react'
import { Toasts } from './components/Toast'
import { createAppTheme, createCssVariablesResolver } from './theme/theme'

export interface UiProviderProps {
  /** App seed colour */
  seed: string
  locale: Locale
  /** Text size preference */
  scale?: number
  /** Translated name for close buttons (screen readers) */
  closeLabel?: string
  /** Always this scheme whatever the device's or the person's: the portable screen is paper to read and sign on */
  forceColorScheme?: 'light' | 'dark'
  children: ReactNode
}

const colorSchemeManager = localStorageColorSchemeManager({ key: 'tnn.color-scheme' })

/** Everything Mantine needs, configured once for the whole platform. */
export function UiProvider({ seed, locale, scale = 1, closeLabel, forceColorScheme, children }: UiProviderProps) {
  const theme = useMemo(() => createAppTheme({ seed, scale, closeLabel }), [seed, scale, closeLabel])
  const resolver = useMemo(() => createCssVariablesResolver(seed), [seed])
  return (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={resolver}
      colorSchemeManager={colorSchemeManager}
      defaultColorScheme="auto"
      forceColorScheme={forceColorScheme}
    >
      <DatesProvider settings={{ locale, firstDayOfWeek: 0, weekendDays: [0, 6] }}>
        <ModalsProvider>
          <NavigationProgress size={3} />
          <Toasts />
          {children}
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  )
}
